import {useCAPIQuery} from './useCAPIQuery'
import {SessionState, type Session} from '../types/session'
import {useCurrentRepository} from '@github-ui/current-repository'
import {usePullContext} from '../contexts/PullContext'
import {fetchCAPI} from '../utils/fetch-capi'

interface UseAgentSessionsProps {
  initialData: Session[] | undefined
  sessionsPollingInterval: number
}

export function useSessions({initialData, sessionsPollingInterval}: UseAgentSessionsProps) {
  const repo = useCurrentRepository()
  const {
    pull: {id: pullId},
  } = usePullContext()
  return useCAPIQuery<Session[]>({
    queryKey: ['agent-sessions', repo.ownerLogin, repo.name, pullId],
    queryFn: async () => {
      const response = await fetchCAPI({
        path: `/agents/sessions/resource/pull/${pullId}`,
      })
      const data = await response.json()
      const sessions = data.sessions as Session[]
      return sessions.sort((a, b) => {
        // Sort sessions by creation date, most recent first
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    },
    initialData,
    refetchInterval: query => {
      const data = query.state.data
      // If no data yet, use default interval
      if (!data) return sessionsPollingInterval

      // Check if any sessions are in active/running state where we want to poll at the default rate
      const hasActiveSessions = data.some(
        session =>
          session.state === SessionState.InProgress ||
          session.state === SessionState.Idle ||
          session.state === SessionState.WaitingForUser,
      )

      // Slow down polling by a factor of 10 if there are no active sessions. Will speed back up
      // as soon as a session becomes active.
      return hasActiveSessions ? sessionsPollingInterval : sessionsPollingInterval * 10
    },
  })
}
